/**
 * ScamBlocker AI — Topbar
 * Org switcher, tema toggle, kullanıcı menüsü, bildirimler.
 */

import { Bell, LogOut, Menu, Monitor, Moon, Search, Sun, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient, { getRefreshToken } from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import { type AppLang, setAppLang } from '@/shared/lib/i18n';
import { useAuthStore } from '@/shared/stores/authStore';
import { useThemeStore } from '@/shared/stores/themeStore';
import { useUIStore } from '@/shared/stores/uiStore';
import styles from './Topbar.module.css';

export function Topbar() {
  const { t, i18n } = useTranslation();
  // Dil değiştirici (2026-08-27): tarayıcısı İngilizce olan Türk kullanıcı
  // TR'ye dönemiyordu — setAppLang hiçbir UI'dan çağrılmıyordu.
  const currentLang: AppLang = i18n.language?.startsWith('en') ? 'en' : 'tr';
  const nextLang: AppLang = currentLang === 'tr' ? 'en' : 'tr';
  const staff = useAuthStore((s) => s.staff);
  const org = useAuthStore((s) => s.org);
  const logout = useAuthStore((s) => s.logout);
  const { mode, setMode } = useThemeStore();
  const { setSidebarMobileOpen, setCommandPaletteOpen } = useUIStore();

  const handleLogout = async () => {
    try {
      const rt = getRefreshToken();
      await apiClient.post(API.org.logout, { refresh_token: rt });
    } catch {
      // ignore
    }
    logout();
  };

  const themeIcons = {
    light: <Sun size={16} />,
    dark: <Moon size={16} />,
    system: <Monitor size={16} />,
  };

  const nextTheme = { light: 'dark', dark: 'system', system: 'light' } as const;

  return (
    <header className={styles.topbar}>
      {/* Mobile hamburger */}
      <button
        type="button"
        className={styles.mobileMenu}
        onClick={() => setSidebarMobileOpen(true)}
        aria-label={t('topbar.openMenu')}
      >
        <Menu size={20} />
      </button>

      {/* Search / Command Palette trigger */}
      <button
        type="button"
        className={styles.searchBtn}
        onClick={() => setCommandPaletteOpen(true)}
      >
        <Search size={16} />
        <span className={styles.searchText}>{t('topbar.search')}</span>
        <kbd className={styles.kbd}>⌘K</kbd>
      </button>

      <div className={styles.spacer} />

      {/* Notifications */}
      <button type="button" className={styles.iconBtn} aria-label={t('topbar.notifications')}>
        <Bell size={18} />
      </button>

      {/* Language toggle */}
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setAppLang(nextLang)}
        aria-label={t('topbar.language', { lang: nextLang.toUpperCase() })}
        title={t('topbar.language', { lang: nextLang.toUpperCase() })}
      >
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.02em' }}>
          {currentLang.toUpperCase()}
        </span>
      </button>

      {/* Theme toggle */}
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setMode(nextTheme[mode])}
        aria-label={t('topbar.theme', { mode })}
        title={t('topbar.theme', { mode })}
      >
        {themeIcons[mode]}
      </button>

      {/* User menu */}
      <div className={styles.userMenu}>
        <div className={styles.avatar}>
          <User size={16} />
        </div>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{staff?.full_name ?? staff?.email ?? '—'}</span>
          <span className={styles.userRole}>
            {staff?.role ?? '—'} • {org?.name ?? ''}
          </span>
        </div>
        <button
          type="button"
          className={styles.logoutBtn}
          onClick={handleLogout}
          aria-label={t('topbar.logout')}
          title={t('topbar.logout')}
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
